import { parseFormula } from "./parser";
import { FormulaEvaluator } from "./evaluator";
import { isMissing } from "./type-registry";
import { buildTableValue } from "./materialize-table";
import { SCALAR_UNIT, TIMESTAMP_UNIT_SECONDS, getDisplaySymbol, } from "../unit-system";
// A duration/timestamp-flavoured reference enters the evaluator as epoch
// seconds carrying the magic timestamp unit, matching the runtime.
function isTimestampKind(kind) {
    return kind === "TIMESTAMP" || kind === "datetime" || kind === "timestamp";
}
function placeholderScalar(unit, kind) {
    return {
        type: "scalar",
        // A non-zero placeholder: unit propagation is value-independent, but 1
        // avoids spurious division-by-zero on `x / ref`.
        value: 1,
        unit: isTimestampKind(kind) ? TIMESTAMP_UNIT_SECONDS : unit ?? SCALAR_UNIT,
    };
}
/**
 * A one-row placeholder table for a TABULAR_DATA local, so a formula that reads
 * a column (`avg(data.flow)`, `last(data).x`) can infer its result unit/type
 * without real rows. Each column carries its declared unit and a dummy value.
 */
function placeholderTable(ref) {
    const cols = ref.tabularColumns ?? [];
    const columns = cols.map((c) => ({
        code: c.code,
        name: c.code,
        data_type: c.kind ?? "number",
        unit: c.unit ?? null,
        is_index: false,
    }));
    const cells = {};
    for (const c of cols) {
        cells[c.code] =
            c.kind === "datetime" ? new Date(0) : c.kind === "string" ? "" : 1;
    }
    return buildTableValue(columns, [{ cells }]);
}
function objectOf(refs) {
    const value = {};
    for (const ref of refs) {
        value[ref.code] = placeholderScalar(ref.unit, ref.kind);
    }
    return { type: "object", value };
}
/**
 * Build the placeholder variable scope an inference run evaluates against. It
 * mirrors the identifier model the resolver (and therefore the runtime
 * evaluator) uses: locals are top-level identifiers, `upstream.<code>`,
 * samples as `<abbr>.<testCode>.<attrCode>` (→ an array), and for tabular
 * columns `row`/`prev` objects plus the `elapsed` duration. Every leaf carries
 * its declared unit but a dummy value, so the evaluator's unit algebra yields
 * the formula's result unit without any real data.
 */
function buildScope(spec) {
    const vars = new Map();
    for (const ref of spec.scopes.local) {
        // Tabular-data locals are tables accessed by column; model them as a
        // one-row placeholder table so column aggregates/reads infer their unit.
        if (ref.kind === "TABULAR_DATA") {
            vars.set(ref.code, placeholderTable(ref));
            continue;
        }
        vars.set(ref.code, placeholderScalar(ref.unit, ref.kind));
    }
    if (spec.scopes.upstream) {
        vars.set("upstream", objectOf(spec.scopes.upstream));
    }
    // Samples mirror the runtime shape: each abbreviation is a top-level
    // variable holding testCode → attrCode → array of replicate values.
    for (const sample of spec.scopes.samples ?? []) {
        const tests = {};
        for (const test of sample.tests) {
            const codes = {};
            for (const attr of test.attrs) {
                // A sample attribute resolves to an array (one value per replicate);
                // aggregates reduce it to the element's unit.
                codes[attr.code] = {
                    type: "array",
                    value: [{ value: 1, unit: attr.unit ?? SCALAR_UNIT }],
                };
            }
            tests[test.code] = { type: "object", value: codes };
        }
        vars.set(sample.abbreviation, { type: "object", value: tests });
    }
    const tabular = spec.scopes.tabular;
    if (tabular) {
        const rowProps = {};
        for (const col of tabular.columns) {
            rowProps[col.code] =
                col.kind === "string"
                    ? { type: "text", value: "" }
                    : placeholderScalar(col.unit, col.kind);
        }
        const rowObject = { type: "object", value: rowProps };
        vars.set("row", rowObject);
        vars.set("prev", rowObject);
        if (tabular.index) {
            // A timestamp index yields a seconds duration; a numeric index keeps its
            // own unit (mirrors computeElapsed).
            vars.set("elapsed", isTimestampKind(tabular.index.kind)
                ? { type: "scalar", value: 1, unit: "s" }
                : placeholderScalar(tabular.index.unit, tabular.index.kind));
        }
    }
    return vars;
}
/**
 * Statically infer the unit a formula's result carries, using only the units
 * declared in the formula context (no row/attribute data). Returns the unit
 * symbol when the result is a concrete dimensional scalar; `undefined` when the
 * formula doesn't parse/evaluate, isn't a scalar, or is dimensionless or a
 * timestamp — i.e. whenever inference can't confidently name a unit (composite
 * results collapse to dimensionless and are intentionally not reported).
 *
 * Intended for an editor-side "declared result unit disagrees with what the
 * formula produces" warning. It fails open: any uncertainty yields `undefined`
 * so the caller simply shows no warning.
 */
export function inferResultUnit(formula, spec) {
    if (!formula.trim())
        return undefined;
    let ast;
    try {
        ast = parseFormula(formula).ast;
    }
    catch {
        return undefined;
    }
    const result = new FormulaEvaluator(buildScope(spec)).evaluate(ast);
    // An EvalError has no `value` key; anything else is an EvalValue.
    if (!("value" in result))
        return undefined;
    if (result.type !== "scalar")
        return undefined;
    const unit = result.unit;
    if (!unit || unit === SCALAR_UNIT || unit === TIMESTAMP_UNIT_SECONDS) {
        return undefined;
    }
    return unit;
}
/**
 * The warnings a template formula would raise when evaluated, found against
 * placeholder data the same way {@link inferResultUnit} works, e.g. adding mL
 * to µL as entered. For the editor, so the author sees them while writing.
 * Empty when there are none or the formula does not evaluate.
 *
 * Table columns are evaluated strictly (mixed units stay an error there), so
 * they never get a warning: it would claim the value still computes.
 */
export function inferFormulaWarnings(formula, spec) {
    if (!formula.trim())
        return [];
    if (spec.kind === "harvest-tabular-column" || spec.kind === "test-tabular-column") {
        return [];
    }
    let ast;
    try {
        ast = parseFormula(formula).ast;
    }
    catch {
        return [];
    }
    const warnings = [];
    new FormulaEvaluator(buildScope(spec), undefined, warnings).evaluate(ast);
    return warnings;
}
/**
 * Statically infer the *type* (and unit, when dimensional) a formula's result
 * carries, evaluating against placeholder data the same way {@link inferResultUnit}
 * does. Returns `undefined` when the formula doesn't parse, errors, or reduces to
 * a blank value — so the caller (an inlay hint) simply shows nothing rather than
 * something misleading. Unlike `inferResultUnit` it reports non-scalar shapes
 * (list/row/table/boolean) too, mirroring what the resolver flags as a non-final
 * result.
 */
export function inferResultSignature(formula, spec) {
    if (!formula.trim())
        return undefined;
    let ast;
    try {
        ast = parseFormula(formula).ast;
    }
    catch {
        return undefined;
    }
    const result = new FormulaEvaluator(buildScope(spec)).evaluate(ast);
    // An EvalError has no `value` key; a blank input propagates to `missing`.
    if (!("value" in result) || isMissing(result))
        return undefined;
    switch (result.type) {
        case "boolean":
            return { kind: "boolean", label: "true/false" };
        case "array":
            return { kind: "list", label: "list" };
        case "object":
            return { kind: "row", label: "row" };
        case "table":
            return { kind: "table", label: "table" };
        case "scalar": {
            const unit = result.unit;
            if (unit === TIMESTAMP_UNIT_SECONDS)
                return { kind: "time", label: "time" };
            if (!unit || unit === SCALAR_UNIT)
                return { kind: "number", label: "number" };
            return { kind: "number", unit, label: getDisplaySymbol(unit) };
        }
        default:
            return undefined;
    }
}
//# sourceMappingURL=infer-unit.js.map